import { Injectable, inject } from '@angular/core';
import { QueryClient, mutationOptions, queryOptions } from '@tanstack/angular-query-experimental';
import { firstValueFrom } from 'rxjs';
import { BackendService } from './backend.service';
import { bucketQueryKeys, categorizationRuleQueryKeys, groupQueryKeys, transactionQueryKeys } from './query-keys';

export type CategorizationRuleTransactionType = 'EXPENSE' | 'INCOME';
export type CategorizationRuleSuggestionStatus = 'ACTIVE' | 'ACCEPTED' | 'DISMISSED' | 'STALE';
export type CategorizationRuleSuggestionListStatus = 'ACTIVE' | 'DISMISSED';

export interface CategorizationRule {
  id: string;
  accountId: string;
  createdByUserId: string;
  updatedByUserId: string;
  merchant: string;
  transactionType: CategorizationRuleTransactionType;
  amountMin: number | null;
  amountMax: number | null;
  bucketId: string;
  enabled: boolean;
  /** Plaid's logo for the merchant, when one is known. */
  merchantLogoUrl: string | null;
  sourceSuggestionId: string | null;
  archivedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A merchant name from autocomplete, with the logo a rule for it would be saved with. */
export interface MerchantSuggestion {
  name: string;
  logoUrl: string | null;
}

export interface CategorizationRuleSuggestionEvidence {
  sampleTransactionIds: string[];
  transactionCount: number;
  amountMin: number;
  amountMax: number;
  plaidCategoryIds: string[];
  sourceWindow: { from: string; to: string };
}

export interface RuleSuggestionProposal {
  action: 'create' | 'update';
  targetRuleId: string | null;
  amountMin: number | null;
  amountMax: number | null;
  currentAmountMin: number | null;
  currentAmountMax: number | null;
  /** The target rule's budget; differs from the suggestion's when the update moves the rule. */
  currentBucketId: string | null;
  token: string;
}
export interface CategorizationRuleSuggestion {
  proposal?: RuleSuggestionProposal;
  id: string;
  accountId: string;
  merchant: string;
  transactionType: CategorizationRuleTransactionType;
  amountMin: number | null;
  amountMax: number | null;
  bucketId: string;
  merchantLogoUrl: string | null;
  fingerprint: string;
  status: CategorizationRuleSuggestionStatus;
  generatedAt: string;
  lastSeenAt: string;
  dismissedAt?: string | null;
  acceptedRuleId: string | null;
  evidence: CategorizationRuleSuggestionEvidence;
}

export interface CategorizationRuleDraft {
  merchant: string;
  transactionType: CategorizationRuleTransactionType;
  amountMin: number | null;
  amountMax: number | null;
  bucketId: string;
  enabled?: boolean;
  sourceSuggestionId?: string | null;
}

export interface CategorizationRulePatch {
  merchant?: string;
  transactionType?: CategorizationRuleTransactionType;
  amountMin?: number | null;
  amountMax?: number | null;
  bucketId?: string;
  enabled?: boolean;
  archived?: boolean;
  deleted?: boolean;
}

export interface CategorizationRuleBackfillTransaction {
  id: string;
  merchant: string;
  amount: number;
  date: string;
  type: CategorizationRuleTransactionType;
}

export interface CategorizationRuleBackfillPreview {
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  data: CategorizationRuleBackfillTransaction[];
}

export interface CategorizationRuleBackfillApplyResult {
  categorizedCount: number;
}

const BACKFILL_PREVIEW_PAGE_SIZE = 5;

@Injectable({ providedIn: 'root' })
export class CategorizationRuleService {
  private readonly backend = inject(BackendService);
  private readonly queryClient = inject(QueryClient);

  rulesQueryOptions() {
    return queryOptions({
      queryKey: categorizationRuleQueryKeys.list(),
      queryFn: () => this.fetchRules(),
      staleTime: 0,
    });
  }

  suggestionsQueryOptions(status: CategorizationRuleSuggestionListStatus = 'ACTIVE') {
    return queryOptions({
      queryKey: categorizationRuleQueryKeys.suggestions(status),
      queryFn: () => this.fetchSuggestions(status),
      staleTime: 30_000,
      refetchInterval: status === 'ACTIVE' ? 30_000 : false,
    });
  }

  backfillPreviewQueryOptions(ruleId: string) {
    return queryOptions({
      queryKey: categorizationRuleQueryKeys.backfillPreview(ruleId),
      queryFn: () => this.fetchBackfillPreview(ruleId),
      staleTime: 0,
    });
  }

  merchantAutocompleteQueryOptions(query: string) {
    return queryOptions({
      queryKey: categorizationRuleQueryKeys.merchantAutocomplete(query),
      queryFn: () => this.fetchMerchantAutocomplete(query),
      staleTime: 60_000,
    });
  }

  createRuleMutationOptions() {
    return mutationOptions({
      mutationKey: [...categorizationRuleQueryKeys.all, 'create'] as const,
      mutationFn: (draft: CategorizationRuleDraft) => this.createRule(draft),
      onSuccess: () => this.invalidateRulesAndSuggestions(),
    });
  }

  updateRuleMutationOptions() {
    return mutationOptions({
      mutationKey: [...categorizationRuleQueryKeys.all, 'update'] as const,
      mutationFn: (input: { ruleId: string; patch: CategorizationRulePatch }) =>
        this.updateRule(input.ruleId, input.patch),
      onSuccess: () => this.invalidateRulesAndSuggestions(),
    });
  }

  acceptSuggestionMutationOptions() {
    return mutationOptions({
      mutationKey: [...categorizationRuleQueryKeys.all, 'accept-suggestion'] as const,
      mutationFn: (input: { suggestionId: string; expectedProposalToken: string }) => this.acceptSuggestion(input),
      onSuccess: () => this.invalidateRulesAndSuggestions(),
    });
  }

  dismissSuggestionMutationOptions() {
    return mutationOptions({
      mutationKey: [...categorizationRuleQueryKeys.all, 'dismiss-suggestion'] as const,
      mutationFn: (suggestionId: string) => this.dismissSuggestion(suggestionId),
      onSuccess: () => this.invalidateSuggestions(),
    });
  }

  restoreSuggestionMutationOptions() {
    return mutationOptions({
      mutationKey: [...categorizationRuleQueryKeys.all, 'restore-suggestion'] as const,
      mutationFn: (suggestionId: string) => this.restoreSuggestion(suggestionId),
      onSuccess: () => this.invalidateSuggestions(),
    });
  }

  applyBackfillMutationOptions() {
    return mutationOptions({
      mutationKey: [...categorizationRuleQueryKeys.all, 'apply-backfill'] as const,
      mutationFn: (ruleId: string) => this.applyBackfill(ruleId),
      onSuccess: (_result, ruleId) => {
        void this.queryClient.invalidateQueries({ queryKey: categorizationRuleQueryKeys.backfillPreview(ruleId) });
        // Backfill moves transactions into a budget, so bucket totals and the
        // group summary are stale as well.
        void this.queryClient.invalidateQueries({ queryKey: transactionQueryKeys.all });
        void this.queryClient.invalidateQueries({ queryKey: bucketQueryKeys.all });
        void this.queryClient.invalidateQueries({ queryKey: groupQueryKeys.all });
      },
    });
  }

  /** Pauses a rule outside a component's lifetime (e.g. from a toast action after the drawer closed). */
  async pauseRule(ruleId: string): Promise<CategorizationRule | null> {
    const rule = await this.updateRule(ruleId, { enabled: false });
    await this.invalidateRulesAndSuggestions();
    return rule;
  }

  private invalidateRulesAndSuggestions(): Promise<void> {
    return this.queryClient.invalidateQueries({ queryKey: categorizationRuleQueryKeys.all });
  }

  private invalidateSuggestions(): Promise<void> {
    return Promise.all([
      this.queryClient.invalidateQueries({ queryKey: categorizationRuleQueryKeys.suggestions('ACTIVE') }),
      this.queryClient.invalidateQueries({ queryKey: categorizationRuleQueryKeys.suggestions('DISMISSED') }),
    ]).then((): void => undefined);
  }

  private fetchRules(): Promise<CategorizationRule[]> {
    return firstValueFrom(
      this.backend.request<CategorizationRule[]>({ type: 'get', apiRoute: 'categorization-rules' }),
    );
  }

  private fetchSuggestions(status: CategorizationRuleSuggestionListStatus): Promise<CategorizationRuleSuggestion[]> {
    return firstValueFrom(
      this.backend.request<CategorizationRuleSuggestion[]>({
        type: 'get',
        apiRoute: 'categorization-rule-suggestions',
        params: { status },
        // Reads persisted proposals; history refresh runs in BullMQ.
        timeoutMillis: 20_000,
      }),
    );
  }

  private fetchBackfillPreview(ruleId: string): Promise<CategorizationRuleBackfillPreview> {
    return firstValueFrom(
      this.backend.request<CategorizationRuleBackfillPreview>({
        type: 'get',
        apiRoute: `categorization-rules/${ruleId}/matching-transactions/preview`,
        params: { pageNumber: 1, pageSize: BACKFILL_PREVIEW_PAGE_SIZE },
      }),
    );
  }

  private fetchMerchantAutocomplete(query: string): Promise<MerchantSuggestion[]> {
    return firstValueFrom(
      this.backend.request<{ data: Array<MerchantSuggestion | string> }>({
        type: 'get',
        apiRoute: 'categorization-rules/merchants/autocomplete',
        params: { query, limit: 8 },
      }),
    ).then(response =>
      // Servers before #1103 return bare names. Web and server deploy separately, so
      // accept both until that server version is gone, then drop the string case.
      response.data.map(entry => (typeof entry === 'string' ? { name: entry, logoUrl: null } : entry)),
    );
  }

  private createRule(draft: CategorizationRuleDraft): Promise<CategorizationRule> {
    return firstValueFrom(
      this.backend.request<CategorizationRule, CategorizationRuleDraft>({
        type: 'post',
        apiRoute: 'categorization-rules',
        data: draft,
      }),
    );
  }

  private updateRule(ruleId: string, patch: CategorizationRulePatch): Promise<CategorizationRule | null> {
    return firstValueFrom(
      this.backend.request<CategorizationRule | null, CategorizationRulePatch>({
        type: 'patch',
        apiRoute: `categorization-rules/${ruleId}`,
        data: patch,
      }),
    );
  }

  private acceptSuggestion(input: {
    suggestionId: string;
    expectedProposalToken: string;
  }): Promise<CategorizationRule> {
    const { suggestionId } = input;
    return firstValueFrom(
      this.backend.request<CategorizationRule>({
        type: 'post',
        apiRoute: `categorization-rule-suggestions/${suggestionId}/accept`,
        data: { expectedProposalToken: input.expectedProposalToken },
      }),
    );
  }

  private dismissSuggestion(suggestionId: string): Promise<CategorizationRuleSuggestion | null> {
    return firstValueFrom(
      this.backend.request<CategorizationRuleSuggestion | null>({
        type: 'post',
        apiRoute: `categorization-rule-suggestions/${suggestionId}/dismiss`,
      }),
    );
  }

  private restoreSuggestion(suggestionId: string): Promise<CategorizationRuleSuggestion> {
    return firstValueFrom(
      this.backend.request<CategorizationRuleSuggestion>({
        type: 'post',
        apiRoute: `categorization-rule-suggestions/${suggestionId}/restore`,
      }),
    );
  }

  private applyBackfill(ruleId: string): Promise<CategorizationRuleBackfillApplyResult> {
    return firstValueFrom(
      this.backend.request<CategorizationRuleBackfillApplyResult>({
        type: 'post',
        apiRoute: `categorization-rules/${ruleId}/apply`,
        timeoutMillis: 20_000,
      }),
    );
  }
}
